/* Millimili 3.0 — ürün kartı hızlı ekleme (<mm3-quick-add>). Sahibi: koleksiyon/kart ajanı.
 * Markup: snippets/card-pro-mm3-quick-add.liquid · stil: assets/mm3-card.css
 *
 * - Fareli ekranda panel CSS ile hover/odakta görünür; beden kutusu formu gönderir → burada AJAX ile sepete eklenir.
 * - Dokunmatik / dar ekranda "+" → alttan açılan <dialog>: renk (varsa) + beden kutuları.
 * - Kartta renk noktası tıklanınca (card-product-siblings) panelin rengi de değişir.
 * - Sepete ekleme Wonder'ın quick-size-picker.js akışıyla aynı: cart-drawer varsa çekmece açılır.
 */
(() => {
  if (customElements.get("mm3-quick-add")) return;

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

  let sheet = null; // tek paylaşılan alt pencere

  class Mm3QuickAdd extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.data = JSON.parse(this.querySelector("[data-mm3-qa-json]")?.textContent || "{}");
      } catch (e) {
        this.data = {};
      }
      this.data.c = this.data.c || [];
      this.data.s = this.data.s || [];
      this.data.v = this.data.v || [];
      this.color = this.dataset.color || "";
      this.form = this.querySelector("[data-mm3-qa-form]");

      this.form?.addEventListener("submit", (e) => {
        e.preventDefault();
        const btn = e.submitter || this.form.querySelector(".mm3-qa__size:focus");
        if (btn && !btn.disabled) this.add(btn.value, btn);
      });
      // panel içindeki tıklama karta (link) geçmesin
      this.querySelector(".mm3-qa__panel")?.addEventListener("click", (e) => e.stopPropagation());

      this.querySelector("[data-mm3-qa-open]")?.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openSheet(e.currentTarget);
      });

      // renk noktası → panel rengi
      const card = this.closest("card-product-siblings") || this.closest(".card__container");
      if (card) {
        const pick = (e) => {
          const w = e.target.closest && e.target.closest(".color-swatcher--wrapper");
          if (!w || !card.contains(w)) return;
          if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
          const c = w.dataset.colorValue;
          if (c) this.setColor(c);
        };
        card.addEventListener("click", pick, true);
        card.addEventListener("keydown", pick, true);
      }
    }

    variantFor(color, size) {
      return this.data.v.find(
        (v) => (!this.data.c.length || v[0] === color) && (!this.data.s.length || v[1] === size),
      );
    }

    buttonsHtml(color) {
      const title = this.dataset.title || "";
      const addL = this.dataset.addLabel || "Add to cart";
      const soldL = this.dataset.soldLabel || "Sold out";
      if (!this.data.s.length) {
        let v = this.data.v.find((x) => (!this.data.c.length || x[0] === color) && x[3]);
        v = v || this.data.v.find((x) => !this.data.c.length || x[0] === color);
        if (!v) return "";
        const lab = v[3] ? addL : soldL;
        return `<button type="submit" name="id" value="${v[2]}" class="mm3-qa__size mm3-qa__size--wide"${v[3] ? "" : ' disabled aria-disabled="true"'} aria-label="${esc(lab)}: ${esc(title)}${color ? ", " + esc(color) : ""}">${esc(lab)}</button>`;
      }
      return this.data.s
        .map((size) => {
          const v = this.variantFor(color, size);
          if (!v) return "";
          const lab = v[3] ? addL : soldL;
          return `<button type="submit" name="id" value="${v[2]}" class="mm3-qa__size"${v[3] ? "" : ' disabled aria-disabled="true"'} aria-label="${esc(lab)}: ${esc(title)}${color ? ", " + esc(color) : ""}, ${esc(size)}">${esc(size)}</button>`;
        })
        .join("");
    }

    setColor(color) {
      if (!color || color === this.color) return;
      this.color = color;
      this.dataset.color = color;
      if (!this.form) return;
      const qty = '<input type="hidden" name="quantity" value="1">';
      this.form.innerHTML = qty + this.buttonsHtml(color);
      const n = this.form.querySelectorAll(".mm3-qa__size").length;
      if (this.data.s.length) this.form.style.setProperty("--mm3-qa-cols", Math.min(4, Math.max(1, n)));
    }

    /* ---------- alt pencere (dokunmatik) ---------- */
    ensureSheet() {
      if (sheet) return sheet;
      sheet = document.createElement("dialog");
      sheet.className = "mm3-qa-sheet";
      sheet.setAttribute("aria-labelledby", "mm3-qa-sheet-title");
      sheet.innerHTML = `
        <div class="mm3-qa-sheet__head">
          <div>
            <p class="mm3-qa-sheet__kicker" id="mm3-qa-sheet-title"></p>
            <p class="mm3-qa-sheet__name"></p>
          </div>
          <button type="button" class="mm3-qa-sheet__close" data-mm3-qa-close>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19"/></svg>
          </button>
        </div>
        <div class="mm3-qa-sheet__colors" role="group"></div>
        <form class="mm3-qa-sheet__sizes" method="post"></form>`;
      document.body.appendChild(sheet);
      sheet.addEventListener("click", (e) => {
        if (e.target === sheet || e.target.closest("[data-mm3-qa-close]")) sheet.close();
      });
      sheet.addEventListener("close", () => {
        document.body.classList.remove("mm3-qa-sheet-on");
        sheet._owner?._opener?.focus?.();
        sheet._owner = null;
      });
      return sheet;
    }

    renderSheet() {
      const s = this.ensureSheet();
      s.querySelector(".mm3-qa-sheet__kicker").textContent = this.dataset.label || "Quick add";
      s.querySelector(".mm3-qa-sheet__name").textContent = this.dataset.title || "";
      s.querySelector(".mm3-qa-sheet__close").setAttribute("aria-label", this.dataset.closeLabel || "Close");

      const colors = s.querySelector(".mm3-qa-sheet__colors");
      if (this.data.c.length > 1) {
        colors.hidden = false;
        colors.innerHTML = this.data.c
          .map((c) => {
            const on = c === this.color;
            return `<button type="button" aria-pressed="${on}" class="mm3-qa-sheet__color${on ? " is-active" : ""}" data-color="${esc(c)}">${esc(c)}</button>`;
          })
          .join("");
        colors.querySelectorAll("[data-color]").forEach((b) =>
          b.addEventListener("click", () => {
            this.setColor(b.dataset.color);
            this.syncCardColor(b.dataset.color);
            this.renderSheet();
          }),
        );
      } else {
        colors.hidden = true;
        colors.innerHTML = "";
      }

      const form = s.querySelector(".mm3-qa-sheet__sizes");
      form.action = this.form?.getAttribute("action") || "/cart/add";
      form.innerHTML = '<input type="hidden" name="quantity" value="1">' + this.buttonsHtml(this.color);
      form.onsubmit = (e) => {
        e.preventDefault();
        const btn = e.submitter || form.querySelector(".mm3-qa__size:focus");
        if (btn && !btn.disabled) this.add(btn.value, btn);
      };
    }

    // alt pencerede renk seçilince kartın renk noktası da aynı renge geçsin (görsel değişir)
    syncCardColor(color) {
      const card = this.closest("card-product-siblings");
      const w = card && Array.from(card.querySelectorAll(".color-swatcher--wrapper")).find((x) => x.dataset.colorValue === color);
      if (w && !w.classList.contains("active")) w.click();
    }

    openSheet(opener) {
      this._opener = opener;
      this.renderSheet();
      const s = this.ensureSheet();
      s._owner = this;
      if (typeof s.showModal === "function") s.showModal();
      else s.setAttribute("open", "");
      document.body.classList.add("mm3-qa-sheet-on");
    }

    /* ---------- sepete ekleme (quick-size-picker.js ile aynı akış) ---------- */
    add(id, el) {
      if (!id || el?.classList.contains("is-loading")) return;
      el?.classList.add("is-loading");
      el?.setAttribute("aria-busy", "true");

      const cart = document.querySelector("cart-drawer");
      const config = typeof fetchConfig === "function" ? fetchConfig("javascript") : { method: "POST", headers: {} };
      config.headers = config.headers || {};
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const fd = new FormData();
      fd.append("id", id);
      fd.append("quantity", 1);
      let sectionIds = "cart-icon-bubble";
      if (cart && typeof cart.getSectionsToRender === "function") {
        sectionIds = cart.getSectionsToRender().map((s) => s.id).join(",");
      }
      fd.append("sections", sectionIds);
      fd.append("sections_url", window.location.pathname);
      config.body = fd;

      const url = (window.routes && window.routes.cart_add_url) || "/cart/add";
      fetch(url, config)
        .then((r) => r.json())
        .then((res) => {
          const pub = typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined";
          if (res.status) {
            if (pub) publish(PUB_SUB_EVENTS.cartError, { source: "mm3-quick-add", productVariantId: id, errors: res.errors || res.description, message: res.message });
            el?.setAttribute("title", res.description || res.message || "");
            return;
          }
          if (pub) publish(PUB_SUB_EVENTS.cartUpdate, { source: "mm3-quick-add", productVariantId: id, cartData: res });
          if (sheet?.open) sheet.close();
          const isCartPage = document.body.classList.contains("template-cart");
          const cartType = this.dataset.cartType || "drawer";
          if (cart && cartType !== "page" && !isCartPage && typeof cart.renderContents === "function") {
            cart.renderContents(res, !document.body.classList.contains("page-overlay-cart-on"));
          } else if (isCartPage || cartType === "page") {
            window.location.href = (window.routes && window.routes.cart_url) || "/cart";
          } else {
            const bubble = document.getElementById("cart-icon-bubble");
            const html = res?.sections?.["cart-icon-bubble"];
            if (bubble && html) {
              const doc = new DOMParser().parseFromString(html, "text/html");
              const src = doc.querySelector(".shopify-section") || doc.body;
              if (src) bubble.innerHTML = src.innerHTML;
            }
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          el?.classList.remove("is-loading");
          el?.removeAttribute("aria-busy");
        });
    }
  }

  customElements.define("mm3-quick-add", Mm3QuickAdd);
})();
